import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../enviroment/enviroment';

interface PresignedResponse {
  uploadUrl: string;
  publicUrl: string;
  key: string;
}

@Injectable({
  providedIn: 'root'
})
export class LandingS3UploadService {


  private apiUrl = environment.apiUrlLanding;

  constructor(private http: HttpClient) {}

  /**
   * Valida que el archivo sea una imagen válida
   */
  validateImage(file: File): { valid: boolean; error?: string } {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Solo se permiten archivos JPG, PNG y WebP' };
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { valid: false, error: 'El archivo no debe superar los 5MB' };
    }

    return { valid: true };
  }

  /**
   * Obtiene presigned URL del backend
   */
  private async obtenerPermisoParaSubir(
    fileName: string,
    fileType: string,
    orgId: string
  ): Promise<PresignedResponse> {
    console.log(`🔑 Pidiendo permiso para subir: ${fileName}`);

    return firstValueFrom(
      this.http.post<PresignedResponse>(`${this.apiUrl}/landing/presigned-url`, {
        fileName,
        fileType,
        orgId
      })
    );
  }

  /**
   * Sube un archivo a S3 con progreso usando XMLHttpRequest
   */
  private subirArchivoAS3(
    file: File,
    uploadUrl: string,
    onProgress?: (porcentaje: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Escuchar progreso de subida
      xhr.upload.addEventListener('progress', (evento) => {
        if (evento.lengthComputable && onProgress) {
          const porcentaje = (evento.loaded / evento.total) * 100;
          onProgress(porcentaje);
          console.log(`📊 Progreso: ${porcentaje.toFixed(0)}%`);
        }
      });

      // Cuando termine exitosamente
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log('✅ Archivo subido exitosamente');
          resolve();
        } else {
          console.error(`❌ Error HTTP: ${xhr.status}`);
          reject(new Error(`Error HTTP: ${xhr.status}`));
        }
      });

      // Si hay error de red
      xhr.addEventListener('error', () => {
        console.error('❌ Error de red');
        reject(new Error('Error de red al subir'));
      });

      // Configurar y enviar
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  }

  /**
   * Sube múltiples imágenes para landing pages con progreso
   */
  async subirVariasImagenes(
    archivos: File[],
    orgId: string,
    onProgress?: (progresoTotal: number, completadas: number) => void
  ): Promise<Array<{ url: string }>> {

    const resultados: Array<{ url: string }> = [];
    const progresosPorArchivo: number[] = new Array(archivos.length).fill(0);
    let completadas = 0;

    console.log(`🚀 Iniciando subida de ${archivos.length} imágenes para landing pages`);

    for (let i = 0; i < archivos.length; i++) {
      const archivo = archivos[i];

      try {
        // 1. Validar archivo
        const validation = this.validateImage(archivo);
        if (!validation.valid) {
          throw new Error(validation.error);
        }

        console.log(`📤 [${i + 1}/${archivos.length}] Procesando: ${archivo.name}`);

        // 2. Obtener presigned URL
        const { uploadUrl, publicUrl } = await this.obtenerPermisoParaSubir(
          archivo.name,
          archivo.type,
          orgId
        );

        console.log(`✅ Permiso recibido para: ${archivo.name}`);
        console.log(`🌐 URL pública: ${publicUrl}`);

        // 3. Subir archivo con seguimiento de progreso
        await this.subirArchivoAS3(archivo, uploadUrl, (progresoIndividual) => {
          progresosPorArchivo[i] = progresoIndividual;
          const progresoTotal = progresosPorArchivo.reduce((a, b) => a + b, 0) / archivos.length;

          if (onProgress) {
            onProgress(progresoTotal, completadas);
          }
        });

        // 4. Marcar como completada
        completadas++;
        progresosPorArchivo[i] = 100;
        resultados.push({ url: publicUrl });

        console.log(`✅ [${completadas}/${archivos.length}] Completada: ${archivo.name}`);

        // Notificar progreso final
        if (onProgress) {
          const progresoTotal = (completadas / archivos.length) * 100;
          onProgress(progresoTotal, completadas);
        }

      } catch (error) {
        console.error(`❌ Error subiendo imagen ${i} (${archivo.name}):`, error);
        throw error; // Propagar error para que el componente lo maneje
      }
    }

    console.log('🎉 Todas las imágenes subidas exitosamente');
    return resultados;
  }
}
